import React from 'react';
import { gql } from '@apollo/client';
import { useMediaQuery } from 'react-responsive';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Placeholder from 'components/consumer/Placeholder';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import CardCarousel from 'components/consumer/image-slider-standard/components/CardCarousel';
import JBImage from 'components/essentials/JBImage';
import ToggleCTALinkButtonWithoutDesignServices from 'components/essentials/ToggleCTALinkButtonWithoutDesignServices';
import Link from 'components/consumer/Link';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import useScreen from 'hooks/useScreen';
import useIsBrowser from 'hooks/useIsBrowser';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import { addMainImages } from '../collections-page/withState';

const GET_COLLECTIONS_LITE = gql`
  query getCollectionsLite($filters: ProductFiltersInput) {
    getCollections(filters: $filters) {
      collections {
        id
        url: slug
        name
        category_label
        category_description
        category_meta_description
      }
    }
  }
`;

const ConsumerCollectionsSlider = props => {
  const isBrowser = useIsBrowser();

  const {
    config: { heading, subHeading, toggleInputType },
  } = props;

  const isSubHeading = subHeading && subHeading.content;

  const { data, error, loading } = useSuspenseQuery(GET_COLLECTIONS_LITE, {
    fetchPolicy: 'cache-and-network',
    context: { clientTarget: 'alexandria' },
    ssr: false,
  });

  const collections =
    !error &&
    !loading &&
    data.getCollections &&
    data.getCollections.collections.length
      ? addMainImages(
          data.getCollections.collections.map(collection => {
            if (collection.url && !collection.url.startsWith('/')) {
              return { ...collection, url: `/${collection.url}` };
            }

            return collection;
          })
        )
      : [];

  // When showing three-across on a larger browser (before showing four-across)
  // we should show larger images to fill the sapce
  const { isDesktop } = useScreen();

  const isNotCinema = useMediaQuery({
    query: '(max-width: 1280px)',
  });

  // Only show larger images if component has mounted in browser, to prevent
  // SSR hydration errors
  const imageSize = isBrowser && isDesktop && isNotCinema ? 390 : 340;

  const imageSizeProps = {
    width: imageSize,
    height: imageSize,
    trim: 'auto',
  };

  const getLoaders = () =>
    Array.from(Array(4).keys()).map(i => (
      <button
        type="button"
        className={classNames(
          'block float-left w-full !text-gray no-underline hover:!no-underline focus-visible:rounded-[5px] focus-visible:p-[5px] focus-visible:outline-offset-[-2px]',
          'flex-[1_1] flex-col justify-between items-center min-w-[80%] sm:min-w-[33.333%] lg:min-w-[25%]',
          '[&_img]:object-cover [&_img]:block'
        )}
        key={i}
        to="#"
        aria-label="Loading"
      >
        <div className="bg-gray-light8">
          <Placeholder
            loading
            type="rect"
            style={{ ...imageSizeProps, marginLeft: '5px', marginRight: '5px' }}
          />
        </div>

        <h5 className="mt-3 mb-[.312rem] text-xl font-bold">
          <Placeholder
            loading
            style={{
              width: 134,
              height: 22,
              marginLeft: '5px',
              marginRight: '5px',
            }}
          />
        </h5>

        <div className="text-sm text-gray">
          <Placeholder
            loading
            style={{
              width: '100%',
              height: 20,
              marginLeft: '5px',
              marginRight: '5px',
            }}
          />
          <Placeholder
            loading
            style={{
              width: '100%',
              height: 20,
              marginLeft: '5px',
              marginRight: '5px',
            }}
          />
        </div>
      </button>
    ));

  const getChildren = collectionsToRender =>
    collectionsToRender.map(collection => {
      const imageSrc = collection.images[0] && collection.images[0].url;
      const collectionName = collection.name.replace(' Collection', '');

      return (
        <Link
          className={classNames(
            'block float-left w-full !text-gray no-underline hover:!no-underline focus-visible:rounded-[5px] focus-visible:p-[5px] focus-visible:outline-offset-[-2px]',
            'flex-[1_1] flex-col justify-between items-center min-w-[80%] sm:min-w-[33.333%] xl:min-w-[25%]',
            '[&_img]:object-cover [&_img]:block',
            {
              'mx-0 sm:px-2': isNewConsumerApp,
            }
          )}
          key={collection.id}
          to={collection.url}
          aria-label={collectionName}
        >
          <JBImage alt="" {...imageSizeProps} src={imageSrc} />

          <h3 className="mt-3 mb-[.312rem] text-xl font-bold">
            {collectionName}
          </h3>

          <p className="text-sm text-gray">
            {collection.category_description ||
              collection.category_meta_description}
          </p>
        </Link>
      );
    });

  return (
    <FixedWidthContainer noMobilePadding className="ConsumerCollectionsSlider">
      <div className="min-h-[500px] mx-0 my-6 [&_a]:no-underline [&_.slider-control-centerright]:!top-[35%] [&_.slider-control-centerleft]:!top-[35%] [&_.slider-control-centerleft]:!left-[3px] [&_.slider-control-centerright]:!right-[3px]">
        <h2 className="max-[767px]:pl-5 max-[767px]:pr-5 m-0 text-2xl font-bold">
          {(heading && heading.content) || ''}
        </h2>
        {isSubHeading && (
          <p className="max-[767px]:pl-5 max-[767px]:pr-5 font-normal">
            {subHeading.content}
          </p>
        )}
        {isBrowser ? (
          !error && (
            <CardCarousel
              imageHeight={350}
              imageWidth={350}
              navType="Button"
              variant="collections-slider"
              visibleSlides={4}
            >
              {getChildren(collections)}
            </CardCarousel>
          )
        ) : (
          <CardCarousel
            imageHeight={350}
            imageWidth={350}
            navType="Button"
            variant="collections-slider"
            visibleSlides={4}
          >
            {getLoaders()}
          </CardCarousel>
        )}

        {!!toggleInputType && (
          <div className="max-[767px]:pl-5 max-[767px]:pr-5">
            <ToggleCTALinkButtonWithoutDesignServices
              toggleInputType={toggleInputType}
              linkClassName="!pl-0"
            />
          </div>
        )}
      </div>
    </FixedWidthContainer>
  );
};

ConsumerCollectionsSlider.propTypes = {
  config: PropTypes.shape({
    heading: PropTypes.shape({
      content: PropTypes.string,
    }),
    subHeading: PropTypes.shape({
      content: PropTypes.string,
    }),
    toggleInputType: PropTypes.object,
  }),
};

export default ConsumerCollectionsSlider;
